import { V2LabelDetailChartPostResponse } from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import { EnergyData } from './ShowDetailModal';
import { MeterType } from '@/api/energyConsumption';
import { DateType } from '@/pages/EnergyLabel/utils';
import { Themes } from '@maxtropy/components';
import { keepPercentOrLine, keepTwoNull } from '@/utils/utils';

const momTitleFunc = (timeResolution: DateType) => {
  switch (timeResolution) {
    case DateType.DAY:
      return '环比昨日';
    case DateType.WEEK:
      return '环比上周';
    case DateType.MONTH:
      return '环比上月';
    case DateType.YEAR:
      return '环比上年';
    default:
      return '环比';
  }
};

export const getModalChartOption = (
  xLabel: string,
  theme: Themes,
  timeResolution: DateType,
  energyData: EnergyData,
  chartData?: V2LabelDetailChartPostResponse
) => {
  const seriesData = () => {
    const dataQueryPeriodName = chartData?.dataQueryPeriodName;
    if (energyData.energyMediumId === MeterType.ELECTRICAL_ENERGY) {
      return [
        {
          barMaxWidth: 80,
          name: `${dataQueryPeriodName ? dataQueryPeriodName + '-' : ''}尖时段`,
          type: 'bar',
          stack: 'total',
          data: chartData?.chartDetail?.map(item => item.summit),
          itemStyle: {
            color: '#FF6D2C', // 图形颜色
          },
        },
        {
          name: `${dataQueryPeriodName ? dataQueryPeriodName + '-' : ''}峰时段`,
          type: 'bar',
          stack: 'total',
          data: chartData?.chartDetail?.map(item => item.peak),
          itemStyle: {
            color: '#CE90D1', // 图形颜色
          },
        },
        {
          name: `${dataQueryPeriodName ? dataQueryPeriodName + '-' : ''}平时段`,
          type: 'bar',
          stack: 'total',
          data: chartData?.chartDetail?.map(item => item.plain),
          itemStyle: {
            color: '#0DB6D9', // 图形颜色
          },
        },
        {
          name: `${dataQueryPeriodName ? dataQueryPeriodName + '-' : ''}谷时段`,
          type: 'bar',
          stack: 'total',
          data: chartData?.chartDetail?.map(item => item.valley),
          itemStyle: {
            color: '#63BC7F', // 图形颜色
          },
        },
      ];
    }
    return [
      {
        name: `${dataQueryPeriodName ? dataQueryPeriodName + '-' : ''}总用量`,
        type: 'bar',
        barMaxWidth: 80,
        data: chartData?.chartDetail?.map(item => item.total),
        itemStyle: {
          color: '#16DD8E', // 图形颜色
        },
      },
    ];
  };

  const isShowYoy = timeResolution === DateType.WEEK || timeResolution === DateType.MONTH;

  return {
    tooltip: {
      formatter: (params: any) => {
        const dataIndex = params[0].dataIndex;
        const item = chartData?.chartDetail![dataIndex];

        return `
          <div style="font-size:12px;">
            <div>${item!.time}</div>
            <table>
              <tr style="text-align: right;">
                <td style="padding: 0 8px"></td>
                <td style="padding: 0 8px">用能</td>
                <td style="padding: 0 8px">${momTitleFunc(timeResolution)}</td>
                ${isShowYoy ? `<td style="padding: 0 8px">同比上年</td>` : ''}
              </tr>
              <tr style="text-align: right;">
                <td style="padding: 0 8px;text-align: left;">${
                  chartData?.dataQueryPeriodName ? chartData?.dataQueryPeriodName + '-' : ''
                }总用量</td>
                <td style="padding: 0 8px;color: ${
                  theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
                }">
                  ${keepTwoNull(item?.total?.value)}${chartData?.unitName}
                </td>
                <td style="padding: 0 8px;color:${Number(item?.total?.mom ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                  ${keepPercentOrLine(item?.total?.mom)}%
                </td>
                ${
                  isShowYoy
                    ? `<td style="padding: 0 8px;color:${Number(item?.total?.yoy ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                    ${keepPercentOrLine(item?.total?.yoy)}%</td>`
                    : ''
                }
              </tr>
               ${
                 energyData.energyMediumId === MeterType.ELECTRICAL_ENERGY
                   ? params
                       .map((param: any, index: number) => {
                         return `
                  <tr style="text-align: right;">
                    <td style="padding: 0 8px;text-align: left;">${
                      param.seriesName.includes('总用量') ? '' : param.marker
                    }${param.seriesName}</td>
                    <td style="padding: 0 8px;color: ${
                      theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
                    }">
                      ${keepTwoNull(param.value)}${chartData?.unitName}
                    </td>
                    <td style="padding: 0 8px;color:${Number(param.data?.mom ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                      ${keepPercentOrLine(param.data?.mom)}%
                    </td>
                    ${
                      isShowYoy
                        ? `<td style="padding: 0 8px;color:${Number(param.data?.yoy ?? 0) > 0 ? '#E64242' : '#52C41A'}">
                        ${keepPercentOrLine(param.data?.yoy)}%</td>`
                        : ''
                    }
                  </tr>
                  `;
                       })
                       .join('')
                   : ''
               }
            </table>
          </div>
        `;
      },
    },
    backgroundColor: 'transparent',
    dataZoom: [
      {
        type: 'slider',
        bottom: 15,
        left: 40,
        right: 20,
        height: 10,
        textStyle: {
          fontSize: 10,
        },
        labelFormatter: (value: string) => {
          return dayjs(value).format(xLabel);
        },
      },
    ],
    legend: {
      show: energyData.energyMediumId === MeterType.ELECTRICAL_ENERGY,
      right: 10,
      itemHeight: 12,
      itemWidth: 12,
    },
    xAxis: [
      {
        type: 'category',
        axisTick: {
          show: false,
        },
        data: chartData?.chartDetail?.map(i => i.time),
        textStyle: {
          color: 'var(--mx-text-secondary-color)',
        },
        axisLabel: {
          formatter: (name: string) => name.split('（')[0],
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: chartData?.unitName ?? '--',
      },
    ],
    series: [
      ...seriesData(),
      {
        type: 'line',
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          label: {
            formatter: `平均值: {@[1]}${chartData?.unitName}`,
            position: 'insideEndTop',
            color: '#FACC58',
          },
          lineStyle: {
            color: '#FACC58',
            type: 'dashed',
          },
          data: [{ yAxis: keepTwoNull(chartData?.avg) }],
        },
      },
    ],
  };
};
